import React, { useState, useEffect, useCallback, useRef } from 'react';

interface DebugEntry {
    timestamp: number;
    label: string;
    data: Record<string, unknown>;
}

const MAX_ENTRIES = 50;
let entries: DebugEntry[] = [];
let listeners: Set<() => void> = new Set();

// Opt-in state is resolved once at module load rather than per call: debugLog sits on auth
// paths that can run during render, and localStorage reads are synchronous.
// Enable at runtime with toggleDebugOverlay() from the console.
let enabled = false;
if (typeof window !== 'undefined') {
    try {
        enabled = localStorage.getItem('debugOverlay') === 'true';
    } catch {
        // Storage access can be blocked (SecurityError); stay disabled rather than
        // failing the module import.
    }
}

/** Whether the debug overlay is active (opted-in via localStorage). */
function overlayEnabled(): boolean {
    return enabled;
}

/**
 * Log a debug entry that appears in the overlay when enabled.
 *
 * `data` may be a thunk. Prefer the thunk form whenever the payload touches
 * browser globals (sessionStorage/localStorage/document): this module is
 * evaluated during SSR/prerender, and a plain object literal would dereference
 * those globals in Node before the enabled-check below can bail out.
 */
export function debugLog(label: string, data: Record<string, unknown> | (() => Record<string, unknown>)) {
    if (!overlayEnabled()) return;

    const resolved = typeof data === 'function' ? data() : data;

    entries = [...entries, { timestamp: Date.now(), label, data: resolved }].slice(-MAX_ENTRIES);
    listeners.forEach(fn => fn());
}

/** Check if debug overlay is enabled. */
export function isDebugEnabled(): boolean {
    return overlayEnabled();
}

/** Toggle debug overlay on/off. Call from console: toggleDebugOverlay() */
if (typeof window !== 'undefined') {
    (window as any).toggleDebugOverlay = () => {
        enabled = !enabled;
        localStorage.setItem('debugOverlay', String(enabled));
        listeners.forEach(fn => fn());
        console.log(`Debug overlay ${enabled ? 'enabled' : 'disabled'}.`);
    };
}

export const DebugOverlay: React.FC = () => {
    const [enabled, setEnabled] = useState(overlayEnabled);
    const [, forceUpdate] = useState(0);
    const [minimized, setMinimized] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const listener = () => {
            setEnabled(overlayEnabled());
            forceUpdate(n => n + 1);
        };
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    });

    const handleCopy = useCallback(() => {
        const text = entries.map(e => {
            const time = new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            const dataStr = Object.entries(e.data).map(([k, v]) => `${k}=${v}`).join(' ');
            return `${time} ${e.label} ${dataStr}`;
        }).join('\n');
        navigator.clipboard.writeText(text).catch(() => {});
    }, []);

    const handleClear = useCallback(() => {
        entries = [];
        forceUpdate(n => n + 1);
    }, []);

    if (!enabled) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: 80,
            left: 4,
            right: 4,
            maxHeight: minimized ? 30 : 200,
            overflow: 'hidden',
            background: 'rgba(0, 0, 0, 0.85)',
            color: '#0f0',
            fontSize: '10px',
            fontFamily: 'monospace',
            zIndex: 99999,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 6px', borderBottom: '1px solid #333', flexShrink: 0 }}>
                <span style={{ fontWeight: 'bold' }}>DEBUG</span>
                <span>
                    <button onClick={handleCopy} style={{ background: 'none', border: 'none', color: '#ff0', fontSize: '10px', cursor: 'pointer', marginRight: 8 }}>Copy</button>
                    <button onClick={handleClear} style={{ background: 'none', border: 'none', color: '#ff0', fontSize: '10px', cursor: 'pointer', marginRight: 8 }}>Clear</button>
                    <button onClick={() => setMinimized(!minimized)} style={{ background: 'none', border: 'none', color: '#ff0', fontSize: '10px', cursor: 'pointer' }}>{minimized ? '▲' : '▼'}</button>
                </span>
            </div>
            {!minimized && (
                <div ref={containerRef} style={{ flex: 1, overflowY: 'auto', padding: '2px 6px' }}>
                    {entries.map((entry, i) => {
                        const time = new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                        const dataStr = Object.entries(entry.data).map(([k, v]) => `${k}=${v}`).join(' ');
                        return (
                            <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <span style={{ color: '#888' }}>{time}</span>{' '}
                                <span style={{ color: '#0df' }}>{entry.label}</span>{' '}
                                <span>{dataStr}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
